import { PrivateStreamInfo } from '../services/chatService';

/**
 * Determina si un stream está en estado pendiente de pago
 * @param stream El objeto stream a verificar
 * @returns true si el stream está pendiente, false en caso contrario
 */
export const isPendingStream = (stream: PrivateStreamInfo): boolean => {
  return stream.status === 'pending';
};

/**
 * Determina si un stream está en estado pagado pero no iniciado
 * @param stream El objeto stream a verificar
 * @returns true si el stream está pagado, false en caso contrario
 */
export const isPaidStream = (stream: PrivateStreamInfo): boolean => {
  return stream.status === 'paid';
};

/**
 * Determina si un stream está activo (en progreso)
 * @param stream El objeto stream a verificar
 * @returns true si el stream está activo, false en caso contrario
 */
export const isActiveStream = (stream: PrivateStreamInfo): boolean => {
  return stream.status === 'active';
};

/**
 * Calcula la fecha y hora de inicio de un stream basado en su scheduleInfo
 * @param stream El objeto stream con la información de programación
 * @returns Fecha de inicio, o null si no aplica
 */
export const getStreamStartTime = (stream: PrivateStreamInfo): Date | null => {
  if (stream.status !== 'paid') return null;
  
  const updatedTime = new Date(stream.updatedAt);
  const scheduleMatch = stream.scheduleInfo?.match(/(\d+)\s*([a-zA-Z]+)/);
  
  if (!scheduleMatch) return updatedTime;
  
  const [_, value, unit] = scheduleMatch;
  const numValue = parseInt(value, 10);
  
  const resultTime = new Date(updatedTime);
  
  switch (unit.toLowerCase()) {
    case 'hour':
    case 'hours':
      resultTime.setHours(resultTime.getHours() + numValue);
      break;
    case 'minute':
    case 'minutes':
      resultTime.setMinutes(resultTime.getMinutes() + numValue);
      break;
    case 'day':
    case 'days':
      resultTime.setDate(resultTime.getDate() + numValue);
      break;
    default:
      resultTime.setHours(resultTime.getHours() + 1);
  }
  
  return resultTime;
};

/**
 * Calcula la fecha y hora de inicio potencial para un stream basado en su scheduleInfo
 * Esta función funciona para streams pendientes también, mostrando cuándo comenzaría si se pagara ahora
 * @param stream El objeto stream con la información de programación
 * @returns Fecha potencial de inicio, o null si no se puede calcular
 */
export const getPotentialStartTime = (stream: PrivateStreamInfo): Date | null => {
  if (!stream.scheduleInfo) return null;
  
  const now = new Date();
  const scheduleMatch = stream.scheduleInfo.match(/(\d+)\s*([a-zA-Z]+)/);
  
  if (!scheduleMatch) return now;
  
  const [_, value, unit] = scheduleMatch;
  const numValue = parseInt(value, 10);
  
  const resultTime = new Date(now);
  
  switch (unit.toLowerCase()) {
    case 'hour':
    case 'hours':
      resultTime.setHours(resultTime.getHours() + numValue);
      break;
    case 'minute':
    case 'minutes':
      resultTime.setMinutes(resultTime.getMinutes() + numValue);
      break;
    case 'day':
    case 'days':
      resultTime.setDate(resultTime.getDate() + numValue);
      break;
    default:
      resultTime.setHours(resultTime.getHours() + 1); // Default: 1 hour
  }
  
  return resultTime;
};

/**
 * Verifica si un stream debería comenzar (la hora de inicio se ha cumplido)
 * @param stream El objeto stream a verificar
 * @returns true si el stream debería comenzar, false en caso contrario
 */
export const shouldStreamStart = (stream: PrivateStreamInfo): boolean => {
  if (stream.status !== 'paid') return false;
  
  const startTime = getStreamStartTime(stream);
  if (!startTime) return false;
  
  return new Date() >= startTime;
};

/**
 * Formatea el tiempo restante para el inicio de un stream
 * @param streamStartTime Fecha de inicio del stream
 * @returns String con formato HH:MM:SS o mensaje apropiado
 */
export const formatTimeRemaining = (streamStartTime: Date | null): string => {
  if (!streamStartTime) return 'Iniciando pronto';
  
  const now = new Date();
  const diffMs = streamStartTime.getTime() - now.getTime();
  
  if (diffMs <= 0) return '00:00:00';
  
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffMins = Math.floor((diffMs % 3600000) / 60000);
  const diffSecs = Math.floor((diffMs % 60000) / 1000);
  
  return `${diffHrs.toString().padStart(2, '0')}:${diffMins.toString().padStart(2, '0')}:${diffSecs.toString().padStart(2, '0')}`;
};

/**
 * Formatea un tiempo potencial estimado para mostrar en la UI
 * @param stream El stream a evaluar
 * @returns String con el tiempo estimado formateado
 */
export const getPotentialTimeDisplay = (stream: PrivateStreamInfo): string => {
  if (stream.status !== 'pending') return '';
  
  const potentialStart = getPotentialStartTime(stream);
  if (!potentialStart) return '';
  
  // Mostrar en formato legible
  const options: Intl.DateTimeFormatOptions = { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true
  };
  
  return `Estimado: ${potentialStart.toLocaleTimeString(undefined, options)}`;
};

/**
 * Determina si el usuario es el anfitrión del stream
 * @param stream El objeto stream
 * @param userAddress La dirección del usuario actual
 * @returns true si el usuario es el anfitrión, false en caso contrario
 */
export const isStreamHost = (stream: PrivateStreamInfo, userAddress: string | null): boolean => {
  return !!userAddress && stream.streamerAddress?.toLowerCase() === userAddress.toLowerCase();
};

/**
 * Determina si el usuario es el invitado del stream
 * @param stream El objeto stream
 * @param userAddress La dirección del usuario actual
 * @returns true si el usuario es el invitado, false en caso contrario
 */
export const isStreamGuest = (stream: PrivateStreamInfo, userAddress: string | null): boolean => {
  return !!userAddress && stream.invitedAddress?.toLowerCase() === userAddress.toLowerCase();
};

/**
 * Obtiene información sobre la otra parte en el stream (anfitrión o invitado)
 * @param stream El objeto stream
 * @param userAddress La dirección del usuario actual
 * @returns Objeto con información sobre la otra parte
 */
export const getOtherParty = (stream: PrivateStreamInfo, userAddress: string | null) => {
  const isHost = isStreamHost(stream, userAddress);
  
  if (isHost) {
    return {
      role: 'Host',
      address: stream.invitedAddress,
      username: stream.invitedUsername || `${stream.invitedAddress.substring(0, 6)}...`,
    };
  } else {
    return {
      role: 'Guest',
      address: stream.streamerAddress,
      username: stream.streamerUsername || `${stream.streamerAddress.substring(0, 6)}...`,
    };
  }
};

/**
 * Obtiene un resumen corto pero descriptivo para un stream pendiente
 * @param stream El stream pendiente
 * @param userAddress La dirección del usuario actual
 * @returns Un string con información resumida para mostrar
 */
export const getPendingStreamSummary = (stream: PrivateStreamInfo, userAddress: string | null): string => {
  if (isStreamHost(stream, userAddress)) {
    return `Esperando aceptación de: ${stream.invitedUsername || stream.invitedAddress.substring(0, 6)}...`;
  } else {
    return `Invitación de: ${stream.streamerUsername || stream.streamerAddress.substring(0, 6)}...`;
  }
};

/**
 * Procesa un stream para mostrar información relevante en la UI
 * @param stream El stream original
 * @param userAddress La dirección del usuario actual
 * @returns El stream con propiedades adicionales útiles para la UI
 */
export const processStreamForDisplay = (stream: PrivateStreamInfo, userAddress: string | null) => {
  const streamStartTime = getStreamStartTime(stream);
  const potentialStartTime = getPotentialStartTime(stream);
  const startShouldOccur = shouldStreamStart(stream);
  const isHost = isStreamHost(stream, userAddress);
  const isGuest = isStreamGuest(stream, userAddress);
  const isPendingState = isPendingStream(stream);
  const isPaidState = isPaidStream(stream);
  const isActiveState = isActiveStream(stream);
  
  return {
    ...stream,
    streamStartTime,
    potentialStartTime,
    potentialTimeDisplay: getPotentialTimeDisplay(stream),
    shouldStart: startShouldOccur,
    timeRemaining: formatTimeRemaining(streamStartTime),
    isHost,
    isGuest,
    otherParty: getOtherParty(stream, userAddress),
    isPending: isPendingState,
    isPaid: isPaidState,
    isActive: isActiveState,
    summary: isPendingState ? getPendingStreamSummary(stream, userAddress) : ''
  };
};
